import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { apiClient } from "../../../config/api";
import API_CONFIG from "../../../config/api";

const parseTechniques = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map((t) => t?.id || t?.techniqueId || t).filter(Boolean);
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map((t) => t?.id || t?.techniqueId || t).filter(Boolean);
    } catch {
      return raw.split(/[,\s]+/).filter(Boolean);
    }
  }
  return [];
};

export default function MitreDetectionsCount({
  range = "24h",
  clientId = null,
  incidents = null,
  loadingExternal = false,
  errorExternal = null,
}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const buildData = (rows) => {
    const counts = {};
    rows.forEach((r) => {
      const techs = parseTechniques(r.techniques || r.Techniques);
      techs.forEach((t) => {
        const key = t.toString();
        counts[key] = (counts[key] || 0) + 1;
      });
    });

    return Object.entries(counts)
      .map(([id, value]) => ({ id, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 12)
      .map((item, idx) => ({
        range: item.id,
        value: item.value,
        color: ["#6BD4F2", "#00ffa3", "#00ff89", "#007dff", "#f97316", "#c084fc"][idx % 6],
      }));
  };

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (Array.isArray(incidents)) {
        setLoading(false);
        setError(errorExternal);
        setData(buildData(incidents));
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const params = {};
        if (range) params.range = range;
        if (range.endsWith("d")) {
          const days = parseInt(range.replace("d", ""), 10);
          if (!Number.isNaN(days)) params.days = days;
        }
        const { data } = await apiClient.get(API_CONFIG.ENDPOINTS.INCIDENTS, { params });
        const rows = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        if (!mounted) return;
        setData(buildData(rows));
      } catch (err) {
        if (mounted) {
          setError(err.response?.data?.message || err.message || "Failed to load incidents");
          setData([]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [range, clientId, incidents, errorExternal]);

  return (
    <Box className="mitre-detections">
      <Box sx={{ marginBottom: "16px" }}>
        <Typography variant="h5" className="dashboard__widget-title">
          MITRE Detections
        </Typography>
        <Typography variant="body2" className="dashboard__widget-subtitle">
          Attack techniques mapped to MITRE ATT&CK framework
        </Typography>
      </Box>
      <Box className="mitre-detections__chart">
        {(loading || loadingExternal) && <div className="text-muted">Loading incidents…</div>}
        {(error || errorExternal) && <div className="text-muted">Error: {error || errorExternal}</div>}
        {!loading && !error && data.length === 0 && (
          <div className="text-muted">No MITRE techniques found for selected filters.</div>
        )}
        <ResponsiveContainer width="100%" height={320}>
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 20, right: 30, left: 80, bottom: 5 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#A0AEC0"
              opacity={0.3}
            />
            <XAxis
              type="number"
              stroke="#A0AEC0"
              style={{ fontSize: "10px" }}
              tick={{ fill: "#A0AEC0" }}
              ticks={[0, 10, 20, 30, 40, 50, 60]}
            />
            <YAxis
              type="category"
              dataKey="range"
              stroke="#A0AEC0"
              style={{ fontSize: "10px" }}
              tick={{ fill: "#A0AEC0" }}
              width={60}
            />
            <Bar dataKey="value" radius={[0, 6, 6, 0]}>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
